import React, { useState, useEffect } from 'react';
import { tagMappingService, PendingTagMapping, TagCategory, TagMapping } from '../../services/tagMappingService';
import { getAuth } from 'firebase/auth';
import { Table } from 'reactstrap';
import './TagMappingAdmin.css';

export const TagMappingAdmin: React.FC = () => {
  const [viewMode, setViewMode] = useState<'pending' | 'existing'>('pending');
  const [pendingMappings, setPendingMappings] = useState<PendingTagMapping[]>([]);
  const [existingMappings, setExistingMappings] = useState<TagMapping[]>([]);
  const [editedMappings, setEditedMappings] = useState<Map<string, TagCategory>>(new Map());
  const [deletedMappings, setDeletedMappings] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (viewMode === 'pending') {
      loadPendingMappings();
    } else {
      loadExistingMappings();
    }
    loadStats();
  }, [viewMode]);

  const loadPendingMappings = async () => {
    setLoading(true);
    try {
      const mappings = await tagMappingService.getPendingMappings();
      setPendingMappings(mappings);
    } catch (error) {
      console.error('Error loading pending mappings:', error);
      setMessage({ type: 'error', text: 'Failed to load pending mappings' });
    } finally {
      setLoading(false);
    }
  };

  const loadExistingMappings = async () => {
    setLoading(true);
    try {
      const mappings = await tagMappingService.getAllMappings();
      setExistingMappings(mappings);
    } catch (error) {
      console.error('Error loading existing mappings:', error);
      setMessage({ type: 'error', text: 'Failed to load existing mappings' });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statistics = await tagMappingService.getMappingStats();
      setStats(statistics);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const checkForNewTags = async () => {
    setProcessing(true);
    setMessage({ type: 'info', text: 'Searching for new tags...' });
    
    try {
      const result = await tagMappingService.processNewTags();
      
      if (result.count > 0) {
        setMessage({ 
          type: 'success', 
          text: `Found and processed ${result.count} new tags with Gemini` 
        });
        await loadPendingMappings();
        await loadStats();
      } else {
        setMessage({ type: 'info', text: 'No new tags found' });
      }
    } catch (error) {
      console.error('Error processing new tags:', error);
      setMessage({ type: 'error', text: 'Failed to process new tags' });
    } finally {
      setProcessing(false);
    }
  };

  const handleCategoryChange = (tag: string, newCategory: TagCategory) => {
    setEditedMappings(prev => {
      const updated = new Map(prev);
      updated.set(tag, newCategory);
      return updated;
    });
  };

  const getCurrentCategory = (mapping: PendingTagMapping): TagCategory => {
    return editedMappings.get(mapping.tag) || mapping.category;
  };

  const approveAll = async () => {
    const auth = getAuth();
    if (!auth.currentUser) {
      setMessage({ type: 'error', text: 'You must be logged in to approve mappings' });
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: 'Approving mappings...' });

    try {
      // Apply any edits to the pending mappings
      const mappingsToApprove = pendingMappings.map(mapping => ({
        ...mapping,
        category: getCurrentCategory(mapping)
      }));

      await tagMappingService.approveMappings(mappingsToApprove, auth.currentUser.uid);
      
      setMessage({ 
        type: 'success', 
        text: `Successfully approved ${mappingsToApprove.length} mappings` 
      });
      
      // Clear the pending list and reload
      setPendingMappings([]);
      setEditedMappings(new Map());
      await loadStats();
      
      // Reload cache
      await tagMappingService.loadMappingsToCache(true);
    } catch (error) {
      console.error('Error approving mappings:', error);
      setMessage({ type: 'error', text: 'Failed to approve mappings' });
    } finally {
      setProcessing(false);
    }
  };

  const approveSingle = async (mapping: PendingTagMapping) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      setMessage({ type: 'error', text: 'You must be logged in to approve mappings' });
      return;
    }

    setProcessing(true);
    try {
      const categoryToSave = getCurrentCategory(mapping);
      await tagMappingService.updateMapping(mapping.tag, categoryToSave, auth.currentUser.uid);
      
      // Remove from pending list
      setPendingMappings(prev => prev.filter(m => m.tag !== mapping.tag));
      setEditedMappings(prev => {
        const updated = new Map(prev);
        updated.delete(mapping.tag);
        return updated;
      });
      
      setMessage({ type: 'success', text: `Approved mapping for "${mapping.tag}"` });
      await loadStats();
    } catch (error) {
      console.error('Error approving single mapping:', error);
      setMessage({ type: 'error', text: 'Failed to approve mapping' });
    } finally {
      setProcessing(false);
    }
  };

  const saveExistingMappings = async () => {
    const auth = getAuth();
    if (!auth.currentUser) {
      setMessage({ type: 'error', text: 'You must be logged in to save mappings' });
      return;
    }

    if (editedMappings.size === 0 && deletedMappings.size === 0) {
      setMessage({ type: 'info', text: 'No changes to save' });
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: 'Saving changes...' });

    try {
      // Handle updates
      if (editedMappings.size > 0) {
        const updates = Array.from(editedMappings.entries())
          .filter(([tag]) => !deletedMappings.has(tag)) // Don't update if being deleted
          .map(([tag, category]) => ({ tag, category }));
        
        if (updates.length > 0) {
          await tagMappingService.updateMappings(updates, auth.currentUser.uid);
        }
      }

      // Handle deletions
      if (deletedMappings.size > 0) {
        await tagMappingService.deleteMappings(Array.from(deletedMappings));
      }
      
      const totalChanges = editedMappings.size + deletedMappings.size;
      setMessage({ 
        type: 'success', 
        text: `Successfully saved ${totalChanges} changes` 
      });
      
      // Clear edited and deleted mappings and reload
      setEditedMappings(new Map());
      setDeletedMappings(new Set());
      await loadExistingMappings();
      await loadStats();
      
      // Reload cache
      await tagMappingService.loadMappingsToCache(true);
    } catch (error) {
      console.error('Error saving mappings:', error);
      setMessage({ type: 'error', text: 'Failed to save mappings' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteMapping = (tag: string) => {
    setDeletedMappings(prev => {
      const updated = new Set(prev);
      if (updated.has(tag)) {
        updated.delete(tag);
      } else {
        updated.add(tag);
      }
      return updated;
    });
  };

  const categories: TagCategory[] = ['moods', 'instruments', 'energy', 'period', 'style', 'location', 'other'];

  const getCategoryColor = (category: TagCategory): string => {
    const colors = {
      moods: '#e91e63',
      instruments: '#2196f3',
      energy: '#ff9800',
      period: '#9c27b0',
      style: '#4caf50',
      location: '#795548',
      other: '#607d8b'
    };
    return colors[category];
  };

  const getConfidenceColor = (confidence?: number): string => {
    if (!confidence) return '#999';
    if (confidence >= 0.8) return '#4caf50';
    if (confidence >= 0.6) return '#ff9800';
    return '#f44336';
  };

  return (
    <div className="tag-mapping-admin">
      <div className="admin-header">
        <h2>Tag Mapping Administration</h2>
        <div className="admin-actions">
          <button 
            onClick={checkForNewTags} 
            disabled={processing}
            className="btn btn-info-custom"
          >
            <span>{processing ? 'Processing...' : 'Check for New Tags'}</span>
          </button>
          <button 
            onClick={() => tagMappingService.loadMappingsToCache(true)} 
            className="btn btn-success-custom"
          >
            <span>Refresh Cache</span>
          </button>
        </div>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="btn-icon clear-button-custom">×</button>
        </div>
      )}

      {stats && (
        <div className="stats-section">
          <h3>Mapping Statistics</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Mapped</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.pending}</div>
              <div className="stat-label">Pending Review</div>
            </div>
            {Object.entries(stats.byCategory).map(([category, count]) => (
              <div key={category} className="stat-card">
                <div 
                  className="stat-value" 
                  style={{ color: getCategoryColor(category as TagCategory) }}
                >
                  {count as number}
                </div>
                <div className="stat-label">{category}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="view-toggle">
        <button 
          className={`btn ${viewMode === 'pending' ? 'btn-primary-custom' : 'btn-warning-custom'}`}
          onClick={() => setViewMode('pending')}
        >
          <span>Pending Mappings ({stats?.pending || 0})</span>
        </button>
        <button 
          className={`btn ${viewMode === 'existing' ? 'btn-primary-custom' : 'btn-warning-custom'}`}
          onClick={() => setViewMode('existing')}
        >
          <span>Existing Mappings ({stats?.total || 0})</span>
        </button>
        {viewMode === 'existing' && (
          <input
            type="text"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        )}
      </div>

      {viewMode === 'pending' && pendingMappings.length > 0 && (
        <div className="pending-section">
          <div className="pending-header">
            <h3>Pending Mappings ({pendingMappings.length})</h3>
            <button 
              onClick={approveAll} 
              disabled={processing}
              className="btn btn-success-custom"
            >
              <span>Approve All</span>
            </button>
          </div>
          
          <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
            <Table className="table-tracks" responsive>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Category</th>
                  <th>Confidence</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingMappings.map((mapping, idx) => (
                  <tr key={mapping.tag} className={idx % 2 === 0 ? 'table-alt-row' : ''}>
                    <td>{mapping.tag}</td>
                    <td>
                      <select
                        value={getCurrentCategory(mapping)}
                        onChange={(e) => handleCategoryChange(mapping.tag, e.target.value as TagCategory)}
                        className="category-select"
                        style={{ borderColor: getCategoryColor(getCurrentCategory(mapping)) }}
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </option>
                        ))}
                      </select>
                      {editedMappings.has(mapping.tag) && (
                        <span className="edited-indicator">edited</span>
                      )}
                    </td>
                    <td>
                      <span 
                        className="confidence-badge"
                        style={{ backgroundColor: getConfidenceColor(mapping.confidence) }}
                      >
                        {mapping.confidence ? `${Math.round(mapping.confidence * 100)}%` : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => approveSingle(mapping)}
                        disabled={processing}
                        className="btn btn-sm btn-success-custom"
                      >
                        <span>Approve</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {viewMode === 'existing' && (
        <div className="pending-section">
          <div className="pending-header">
            <h3>Existing Mappings ({existingMappings.length})</h3>
            {(editedMappings.size > 0 || deletedMappings.size > 0) && (
              <button 
                onClick={saveExistingMappings} 
                disabled={processing}
                className="btn btn-success-custom"
              >
                <span>Save Changes ({editedMappings.size + deletedMappings.size})</span>
              </button>
            )}
          </div>
          
          <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
            <Table className="table-tracks" responsive>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Category</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {existingMappings
                  .filter(mapping => 
                    searchTerm === '' || 
                    mapping.tag.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((mapping, idx) => {
                    const isDeleted = deletedMappings.has(mapping.tag);
                    return (
                      <tr key={mapping.tag} className={`${idx % 2 === 0 ? 'table-alt-row' : ''} ${isDeleted ? 'deleted-row' : ''}`}>
                        <td>{mapping.tag}</td>
                        <td>
                          <select
                            value={editedMappings.get(mapping.tag) || mapping.category}
                            onChange={(e) => handleCategoryChange(mapping.tag, e.target.value as TagCategory)}
                            className="category-select"
                            disabled={isDeleted}
                          >
                            {categories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                          {editedMappings.has(mapping.tag) && !isDeleted && (
                            <span className="edited-indicator">edited</span>
                          )}
                          {isDeleted && (
                            <span className="deleted-indicator">deleted</span>
                          )}
                        </td>
                        <td>
                          <span style={{ color: mapping.source === 'manual' ? '#4caf50' : '#ff9800' }}>
                            {mapping.source || 'unknown'}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteMapping(mapping.tag)}
                            className={`btn btn-sm ${isDeleted ? 'btn-warning-custom' : 'btn-danger-custom'}`}
                            title={isDeleted ? 'Restore mapping' : 'Delete mapping'}
                          >
                            <span>{isDeleted ? 'Restore' : 'Delete'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          Loading...
        </div>
      )}
    </div>
  );
};